import { RendererEvent } from './enums/RendererEvent';
import 'reflect-metadata';

type Callback = (...params: any) => void;
type EventsRecord = Partial<Record<RendererEvent, Callback[] | undefined>>;

export type EventOn = (key: RendererEvent, callback: Callback) => void;
export type EventEmit = (key: RendererEvent) => void;
export type EventRemove = (key: RendererEvent, callback: Callback) => void;

export function Emit(event: RendererEvent) {
	return function (
		target: any,
		propertyKey: string,
		descriptor: PropertyDescriptor
	) {
		const originalMethod = descriptor.value;

		descriptor.value = function (...args: any) {
			const result = originalMethod.apply(this, args);

			Events.getInstance().emit(event, result);
			return result;
		};
	};
}

export function ApplyEventHandlers<T extends { new (...args: any[]): {} }>(
	constructor: T
) {
	return class extends constructor {
		constructor(...args: any[]) {
			super(...args);

			const eventHandlers = Reflect.getMetadata(
				'eventHandlers',
				constructor.prototype
			);

			if (eventHandlers) {
				eventHandlers.forEach(
					(handler: { event: RendererEvent; method: Function }) => {
						Events.getInstance().on(handler.event, handler.method.bind(this));
					}
				);
			}
		}
	};
}

export function On(event: RendererEvent) {
	return function (
		target: any,
		propertyKey: string,
		descriptor: PropertyDescriptor
	) {
		const existingEventHandlers =
			Reflect.getMetadata('eventHandlers', target) || [];

		existingEventHandlers.push({
			event,
			method: descriptor.value,
		});

		Reflect.defineMetadata('eventHandlers', existingEventHandlers, target);
	};
}

export class Events {
	private static instance: Events;

	private _events: EventsRecord = {};

	get events() {
		return this._events;
	}

	set events(value: EventsRecord) {
		this._events = value;
	}

	on: EventOn = (key: RendererEvent, callback: Callback) => {
		if (!this.events[key]) this.events[key] = [];
		this.events[key]!.push(callback);
	};

	remove = (key: RendererEvent, callback: () => void) => {
		if (this.events[key] && this.events[key]?.indexOf(callback) !== -1) {
			this.events[key]?.splice(this.events[key]?.indexOf(callback) || -1, 1);
		}
	};

	removeAll = () => {
		this.events = {};
	};

	emit = (key: RendererEvent, ...params: any) => {
		const events = this.events[key] || [];

		for (const callback of events) {
			callback.apply(this, [...params]);
		}
	};

	public static getInstance(): Events {
		if (!Events.instance) {
			Events.instance = new Events();
		}

		return Events.instance;
	}
}
